import React, { useEffect } from "react"
import { Box, FormHelperText, Typography } from "@mui/material"
import { AppAntSwitch } from "@components"
import { NumberFieldSeeker, ButtonLoadingSeeker } from "@ui-seeker/core"
import { batch, useSignal } from "@preact/signals-react"
import { autoEuropeDrawerData, autoEuropeDrawerErrors } from "Pages/AutoEuropeTool/Page/@signals"
import RemovableValueChip from "Pages/AutoEuropeTool/Page/@components/RemovableValueChip"
import { dsColorNew } from "@palette"
import { durationErrors, overlapsAny, OVERLAP_ERROR } from "Pages/AutoEuropeTool/Page/@utils/durations"

const defaultInsurance = () => ({ delta: "", deltaByDuration: [], profitPerDay: [] })

function fieldErrors(fromVal, toVal, minVal, maxVal) {
	const min = Number(minVal), max = Number(maxVal)
	const minErr  = minVal  !== "" && (isNaN(min)  || min  <= 0) ? "> 0"
	              : minVal !== "" && maxVal !== "" && !isNaN(max) && max > 0 && min >= max ? "< Max/day" : null
	const maxErr  = maxVal  !== "" && (isNaN(max)  || max  <= 0) ? "> 0"
	              : maxVal !== "" && minVal !== "" && !isNaN(min) && min > 0 && max <= min ? "> Min/day" : null
	return { ...durationErrors(fromVal, toVal), min: minErr, max: maxErr }
}

function deltaFieldErrors(fromVal, toVal, deltaVal) {
	const delta = Number(deltaVal)
	const deltaErr = deltaVal !== "" && (isNaN(delta) || delta < 0.01) ? "> 0" : null
	return { ...durationErrors(fromVal, toVal), delta: deltaErr }
}

function isInsuranceValid(ins) {
	if (!ins) return true
	return !!ins.delta && Number(ins.delta) >= 0.01 && (ins.profitPerDay || []).length > 0
}

// The on / off of the insurance block, in the header of its section. It also keeps the block's validity,
// so turning insurance off frees Save even when the fields below are no longer on screen.
export function AutoEuropeInsuranceSwitch() {
	const insurance = autoEuropeDrawerData.value.insurance

	useEffect(() => {
		autoEuropeDrawerErrors.value = { ...autoEuropeDrawerErrors.value, insurance: isInsuranceValid(insurance) }
	}, [JSON.stringify(insurance)])

	return <AppAntSwitch
		checked={!!insurance}
		onChange={e => autoEuropeDrawerData.value = {
			...autoEuropeDrawerData.value,
			insurance: e.target.checked ? defaultInsurance() : null,
		}}
	/>
}

export default function AutoEuropeInsurance() {
	const insurance = autoEuropeDrawerData.value.insurance
	const fromInput = useSignal("")
	const toInput   = useSignal("")
	const minInput  = useSignal("")
	const maxInput  = useSignal("")
	const deltaFromInput  = useSignal("")
	const deltaToInput    = useSignal("")
	const deltaValueInput = useSignal("")

	useEffect(() => {
		autoEuropeDrawerErrors.value = { ...autoEuropeDrawerErrors.value, insurance: isInsuranceValid(insurance) }
	}, [JSON.stringify(insurance)])

	const handleDelta = (e) => {
		autoEuropeDrawerData.value = {
			...autoEuropeDrawerData.value,
			insurance: { ...insurance, delta: e.target.value },
		}
	}

	const errs = fieldErrors(fromInput.value, toInput.value, minInput.value, maxInput.value)
	const rangeReady = !!fromInput.value && !!toInput.value && !errs.from && !errs.to
	const overlaps = rangeReady && overlapsAny(insurance?.profitPerDay, Number(fromInput.value), Number(toInput.value))
	const canAdd = rangeReady && !!minInput.value && !!maxInput.value
	              && !errs.min && !errs.max && !overlaps

	const add = () => {
		if (!canAdd) return
		const newItem = {
			durationFrom: Number(fromInput.value),
			durationTo:   Number(toInput.value),
			min:          Number(minInput.value),
			max:          Number(maxInput.value),
		}
		batch(() => {
			const updated = [...(insurance.profitPerDay || []), newItem]
			autoEuropeDrawerData.value = { ...autoEuropeDrawerData.value, insurance: { ...insurance, profitPerDay: updated } }
			fromInput.value = toInput.value = minInput.value = maxInput.value = ""
		})
	}

	const remove = (index) => {
		const updated = (insurance.profitPerDay || []).filter((_, i) => i !== index)
		autoEuropeDrawerData.value = { ...autoEuropeDrawerData.value, insurance: { ...insurance, profitPerDay: updated } }
	}

	const deltaErrs = deltaFieldErrors(deltaFromInput.value, deltaToInput.value, deltaValueInput.value)
	const deltaRangeReady = !!deltaFromInput.value && !!deltaToInput.value && !deltaErrs.from && !deltaErrs.to
	const deltaOverlaps = deltaRangeReady
		&& overlapsAny(insurance?.deltaByDuration, Number(deltaFromInput.value), Number(deltaToInput.value))
	const canAddDelta = deltaRangeReady && !!deltaValueInput.value && !deltaErrs.delta && !deltaOverlaps

	const addDelta = () => {
		if (!canAddDelta) return
		const newItem = {
			durationFrom: Number(deltaFromInput.value),
			durationTo:   Number(deltaToInput.value),
			delta:        Number(deltaValueInput.value),
		}
		batch(() => {
			const updated = [...(insurance.deltaByDuration || []), newItem]
			autoEuropeDrawerData.value = { ...autoEuropeDrawerData.value, insurance: { ...insurance, deltaByDuration: updated } }
			deltaFromInput.value = deltaToInput.value = deltaValueInput.value = ""
		})
	}

	const removeDelta = (index) => {
		const updated = (insurance.deltaByDuration || []).filter((_, i) => i !== index)
		autoEuropeDrawerData.value = { ...autoEuropeDrawerData.value, insurance: { ...insurance, deltaByDuration: updated } }
	}

	return (
		<Box>
			{insurance && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<NumberFieldSeeker
						label="Delta (insurance cost / day)"
						startIcon="shield-halved"
						value={insurance.delta ?? ""}
						onChange={handleDelta}
						error={
							// Con el seguro activo el delta general siempre hace falta (el pydantic lo
							// tipa como requerido), tenga o no rangos: condicionarlo dejaba el Save
							// muerto sin ningún mensaje cuando solo se rellenaban los overrides.
							!insurance.delta || insurance.delta === ""
								? "Required"
								: Number(insurance.delta) < 0.01
									? "Must be > 0"
									: undefined
						}
					/>

					<Box>
						<Typography sx={{ fontSize: 12, fontWeight: 600, color: dsColorNew.textMuted }}>
							Delta override by duration range
						</Typography>
						<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
							Optional. Durations not covered by any range use the Delta above.
						</Typography>
					</Box>

					<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
						<NumberFieldSeeker
							label="Dur. From"
							placeholder="1"
							value={deltaFromInput.value}
							onChange={e => deltaFromInput.value = e.target.value}
							error={deltaErrs.from}
							sx={{ flex: '1 1 55px' }}
						/>
						<NumberFieldSeeker
							label="Dur. To"
							placeholder="7"
							value={deltaToInput.value}
							onChange={e => deltaToInput.value = e.target.value}
							error={deltaErrs.to}
							sx={{ flex: '1 1 55px' }}
						/>
						<NumberFieldSeeker
							label="Delta/day"
							placeholder="0"
							value={deltaValueInput.value}
							onChange={e => deltaValueInput.value = e.target.value}
							error={deltaErrs.delta}
							sx={{ flex: '1 1 55px' }}
						/>
						<ButtonLoadingSeeker
							variant="outlined"
							onClick={addDelta}
							disabled={!canAddDelta}
							sx={{ mt: 1 }}
						>
							Add
						</ButtonLoadingSeeker>
					</Box>

					{deltaOverlaps && <FormHelperText error>{OVERLAP_ERROR}</FormHelperText>}

					{(insurance.deltaByDuration || []).length > 0 && (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							{(insurance.deltaByDuration || []).map((d, i) => (
								<RemovableValueChip
									key={i}
									label={`${d.durationFrom}–${d.durationTo}d · ${d.delta}/day`}
									onDelete={() => removeDelta(i)}
								/>
							))}
						</Box>
					)}

					<Typography sx={{ fontSize: 12, fontWeight: 600, color: dsColorNew.textMuted }}>
						Profit per day by duration range
					</Typography>

					<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
						<NumberFieldSeeker
							label="Dur. From"
							placeholder="1"
							value={fromInput.value}
							onChange={e => fromInput.value = e.target.value}
							error={errs.from}
							sx={{ flex: '1 1 55px' }}
						/>
						<NumberFieldSeeker
							label="Dur. To"
							placeholder="7"
							value={toInput.value}
							onChange={e => toInput.value = e.target.value}
							error={errs.to}
							sx={{ flex: '1 1 55px' }}
						/>
						<NumberFieldSeeker
							label="Min/day"
							placeholder="0"
							value={minInput.value}
							onChange={e => minInput.value = e.target.value}
							error={errs.min}
							sx={{ flex: '1 1 55px' }}
						/>
						<NumberFieldSeeker
							label="Max/day"
							placeholder="0"
							value={maxInput.value}
							onChange={e => maxInput.value = e.target.value}
							error={errs.max}
							sx={{ flex: '1 1 55px' }}
						/>
						<ButtonLoadingSeeker
							variant="outlined"
							onClick={add}
							disabled={!canAdd}
							sx={{ mt: 1 }}
						>
							Add
						</ButtonLoadingSeeker>
					</Box>

					{overlaps && <FormHelperText error>{OVERLAP_ERROR}</FormHelperText>}
					{(insurance.profitPerDay || []).length === 0 && (
						<FormHelperText error>Add at least one profit/day range</FormHelperText>
					)}

					{(insurance.profitPerDay || []).length > 0 && (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							{(insurance.profitPerDay || []).map((d, i) => (
								<RemovableValueChip
									key={i}
									label={`${d.durationFrom}–${d.durationTo}d · ${d.min}–${d.max}/day`}
									onDelete={() => remove(i)}
								/>
							))}
						</Box>
					)}
				</Box>
			)}
		</Box>
	)
}